import { useRef, useState, useEffect } from 'react';
import { CommandBar, ICommandBarItemProps, SearchBox } from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';
import { config } from '@/config';
import { ConfirmDialog } from './ConfirmDialog';
import { ActivityLog } from './ActivityLog';
import { emailService } from '@/services/emailService';

// Build identity shown in the More menu and stamped into support emails.
const buildLabel = config.build.sha
  ? `${config.build.version} (${config.build.sha})`
  : config.build.version;

export function Toolbar() {
  const {
    isLoading,
    selectedItems,
    searchQuery,
    currentSiteId,
    setSearchQuery,
    refresh,
    setCreateFolderOpen,
    deleteSelected,
    downloadSelected,
    openMoveDialog,
    getSelectedItems,
    isDeleteConfirmOpen,
    setDeleteConfirmOpen,
    uploadFiles,
    attachSelectedToEmail,
    currentPath,
    browserView,
    setBrowserView,
    expandedPaths,
    collapseAll,
  } = useFileBrowserStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  // Compose vs read is detected once: the compose-only "Attach" action is the only
  // thing that needs it. The add-in is otherwise a file manager + drop target,
  // independent of which message (if any) is selected.
  const [isComposeMode, setIsComposeMode] = useState(false);

  useEffect(() => {
    setIsComposeMode(emailService.isComposeMode());
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFiles(Array.from(files), currentPath);
      e.target.value = '';
    }
  };

  const handleDeleteClick = () => {
    if (selectedItems.size > 0) {
      setDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmOpen(false);
    await deleteSelected();
  };

  // Shared Outlook action-bar scale: 32px buttons, 16px glyphs (styles prop,
  // not global .ms-* CSS overrides). Disabled state is made unmistakable -
  // Fluent's default dim is too subtle on icon-only commands.
  // Transparent button backgrounds: CommandBarButton defaults to the theme's
  // base background (same slot as the list), which clashes with the bar's
  // --bg-secondary chrome. Transparent means buttons always match their bar.
  const commandButtonStyles = {
    root: { height: 32, minWidth: 32, backgroundColor: 'transparent' },
    rootHovered: { backgroundColor: 'var(--bg-hover)' },
    rootPressed: { backgroundColor: 'var(--bg-hover)' },
    rootExpanded: { backgroundColor: 'var(--bg-hover)' },
    icon: { fontSize: 16 },
    rootDisabled: { backgroundColor: 'transparent', opacity: 0.4 },
    iconDisabled: { color: 'var(--text-secondary)' },
  };

  const items: ICommandBarItemProps[] = [
    {
      key: 'upload',
      buttonStyles: commandButtonStyles,
      text: 'Upload',
      iconOnly: true,
      iconProps: { iconName: 'Upload' },
      title: 'Upload Files',
      onClick: handleUploadClick,
    },
    {
      key: 'newFolder',
      buttonStyles: commandButtonStyles,
      text: 'New Folder',
      iconOnly: true,
      iconProps: { iconName: 'NewFolder' },
      onClick: () => setCreateFolderOpen(true),
      title: 'Create New Folder',
    },
  ];

  // Attach the selected SharePoint file(s) to the message being written. It is
  // compose-only, but stays VISIBLE in read mode (disabled) so the capability is
  // discoverable instead of vanishing. Disabled when not composing or when
  // nothing is selected; the tooltip explains which.
  const canAttach = isComposeMode && selectedItems.size > 0;
  items.push({
    key: 'attachFile',
    buttonStyles: commandButtonStyles,
    text: selectedItems.size > 0 ? `Attach (${selectedItems.size})` : 'Attach File',
    iconOnly: true,
    iconProps: { iconName: 'Attach' },
    disabled: !canAttach,
    onClick: () => {
      attachSelectedToEmail();
    },
    title: !isComposeMode
      ? 'Attach is available while composing an email'
      : selectedItems.size > 0
        ? `Attach (${selectedItems.size})`
        : 'Select files to attach',
  });

  // Selection commands stay visible and disable when nothing is selected
  // (Office convention: disable for "not yet" rather than hide). Attach above
  // follows the same rule and additionally disables outside compose mode. No
  // layout shift on select.
  const hasSelection = selectedItems.size > 0;
  items.push({
    key: 'download',
    buttonStyles: commandButtonStyles,
    text: hasSelection ? `Download (${selectedItems.size})` : 'Download',
    iconOnly: true,
    iconProps: { iconName: 'Download' },
    disabled: !hasSelection,
    onClick: () => {
      downloadSelected();
    },
    title: hasSelection
      ? `Download ${selectedItems.size} selected file(s)`
      : 'Download (select files first)',
  });
  items.push({
    key: 'move',
    buttonStyles: commandButtonStyles,
    text: hasSelection ? `Move (${selectedItems.size})` : 'Move',
    iconOnly: true,
    iconProps: { iconName: 'MoveToFolder' },
    disabled: !hasSelection,
    onClick: () => {
      openMoveDialog(getSelectedItems());
    },
    title: hasSelection
      ? `Move ${selectedItems.size} selected item(s)`
      : 'Move (select items first)',
  });
  items.push({
    key: 'delete',
    text: hasSelection ? `Delete (${selectedItems.size})` : 'Delete',
    iconOnly: true,
    iconProps: { iconName: 'Delete' },
    disabled: !hasSelection,
    onClick: handleDeleteClick,
    buttonStyles: commandButtonStyles,
    title: hasSelection ? `Delete (${selectedItems.size})` : 'Delete (select items first)',
  });

  // Secondary commands collapse into one labeled dropdown on the right -
  // the icon-only Refresh/Activity pair read as lookalikes, and the menu
  // gives every entry a text label.
  const currentSiteUrl = config.sharePoint.sites.find((s) => s.id === currentSiteId)?.webUrl;
  const farItems: ICommandBarItemProps[] = [
    {
      key: 'collapseAll',
      iconOnly: true,
      iconProps: { iconName: 'CollapseContent' },
      ariaLabel: 'Collapse all folders',
      title: 'Collapse all',
      disabled: expandedPaths.size === 0,
      buttonStyles: commandButtonStyles,
      onClick: () => collapseAll(),
    },
    {
      key: 'viewToggle',
      iconOnly: true,
      iconProps: { iconName: browserView === 'favorites' ? 'FavoriteStarFill' : 'FavoriteStar' },
      ariaLabel: browserView === 'favorites' ? 'Show all folders' : 'Show favorite folders',
      title: browserView === 'favorites' ? 'Show all folders' : 'Show favorites',
      toggle: true,
      checked: browserView === 'favorites',
      buttonStyles: {
        ...commandButtonStyles,
        root: {
          ...commandButtonStyles.root,
          backgroundColor: browserView === 'favorites' ? 'var(--bg-hover)' : 'transparent',
        },
      },
      onClick: () => setBrowserView(browserView === 'favorites' ? 'all' : 'favorites'),
    },
    {
      key: 'more',
      buttonStyles: {
        ...commandButtonStyles,
        // The three-dot glyph reads fainter than fuller glyphs at the same
        // size, so it gets the primary text color, not theme blue.
        icon: { fontSize: 16, color: 'var(--text-primary)' },
        iconHovered: { color: 'var(--text-primary)' },
        iconPressed: { color: 'var(--text-primary)' },
        iconExpanded: { color: 'var(--text-primary)' },
        menuIcon: { display: 'none' },
      },
      text: 'More',
      iconOnly: true,
      iconProps: { iconName: 'More' },
      title: 'More',
      subMenuProps: {
        items: [
          {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            disabled: isLoading,
            onClick: () => {
              refresh();
            },
          },
          {
            key: 'activity',
            text: 'Activity Log',
            iconProps: { iconName: 'ClipboardList' },
            onClick: () => setIsActivityOpen(true),
          },
          {
            key: 'openSite',
            text: 'Open SharePoint Site',
            iconProps: { iconName: 'OpenInNewWindow' },
            disabled: !currentSiteUrl,
            onClick: () => {
              if (currentSiteUrl) window.open(currentSiteUrl, '_blank');
            },
          },
          {
            key: 'help',
            text: 'Help & Support',
            iconProps: { iconName: 'Help' },
            onClick: () => {
              // Pre-stamp the support email with the build identity so the
              // helpdesk never has to ask "what version are you on".
              const subject = encodeURIComponent('CRG FileSpace Support');
              const body = encodeURIComponent(`\n\n---\nVersion: ${buildLabel}\n`);
              window.open(
                `mailto:helpdesk@crgglobalinc.com?subject=${subject}&body=${body}`,
                '_blank',
              );
            },
          },
        ],
      },
    },
  ];

  return (
    <div className="toolbar">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <CommandBar items={items} farItems={farItems} className="toolbar-commands" />
      <div className="toolbar-search">
        <SearchBox
          placeholder={currentPath ? 'Search this folder' : 'Search this site'}
          value={searchQuery}
          onChange={(_, value) => setSearchQuery(value || '')}
          onClear={() => setSearchQuery('')}
          disabled={browserView === 'favorites'}
          className="search-box"
        />
      </div>

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title="Delete Items"
        message={`Are you sure you want to delete ${selectedItems.size} selected item(s)?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <ActivityLog isOpen={isActivityOpen} onClose={() => setIsActivityOpen(false)} />
    </div>
  );
}
